import round from 'lodash/round'
import { secondsToFrames } from '../timeline/frames'
import {
  isMediaItemAny,
  itemWindow,
  type MediaItemAny,
  type TimelineConfig,
  type TimelineItemAny,
  type TimelineItemV2,
  type TimelineItemV3,
} from '../types/renderConfigV2'
import {
  eligibleParent,
  followerKind,
  parentOf,
  sceneOf,
  withoutScene,
} from './followers'
import { FPS } from '../types/video'
import { keptLength, piecesOf, type SceneWindow } from './sceneMap'
import type { Range } from './skipRanges'

/**
 * Scenes follow the live transcript (#77).
 *
 * A scene stores its window; its length is the window minus the
 * transcript's cuts (`sceneMap.ts`). When the cuts change — a word removed or
 * restored, a `skipSegments` entry — `syncScenes` re-derives every scene's
 * length, and the ripple moves what follows so the tracks stay lined up. One
 * algorithm, two adapters: `syncConfigScenes` over a persisted v2 config
 * (seconds; `getProject` and `renderProject`) and the editor's over its
 * undoable state (frames; `transcriptSync.ts`).
 *
 * The ripple is computed from the PRE-sync snapshot (`rippleOf`): every
 * recording scene, changed or not, is a constraint at its old end, and what
 * follows moves just far enough that no scene on any track is cut short.
 * Scenes in sequence add up; scenes side by side on parallel tracks do not,
 * so a lane whose scene did not shrink keeps a gap rather than losing media.
 * An item's start and end move separately, so an item spanning a changed
 * scene (a background, a visualizer, a follower) stretches with it. Changes
 * are compared in frames, so rounding never makes a no-op sync look like an
 * edit.
 */

export type SyncScene = {
  id: string
  recordingId: string
  window: SceneWindow
  /** The scene's current length, in frames. */
  durationInFrames: number
}

export type SceneSyncResult = {
  /** Scenes whose kept length changed: the new length. */
  lengths: Map<string, { seconds: number; frames: number }>
  /** Scenes with nothing left: every word in the window was cut. */
  removed: string[]
}

/**
 * Re-derives each scene's kept length from its recording's cuts. A recording
 * missing from `skipsByRecording` is unknown (its transcript could not be
 * read) and its scenes are left alone; `[]` means it has no cuts.
 */
export function syncScenes(
  scenes: readonly SyncScene[],
  skipsByRecording: Readonly<Record<string, readonly Range[] | undefined>>,
  fps: number = FPS,
): SceneSyncResult {
  const lengths = new Map<string, { seconds: number; frames: number }>()
  const removed: string[] = []
  for (const scene of scenes) {
    const skips = skipsByRecording[scene.recordingId]
    if (!skips) continue
    const pieces = piecesOf(scene.window, skips)
    if (!pieces.length) {
      removed.push(scene.id)
      continue
    }
    const seconds = keptLength(pieces)
    const frames = Math.max(1, secondsToFrames(seconds, fps))
    if (frames !== scene.durationInFrames)
      lengths.set(scene.id, { seconds, frames })
  }
  return { lengths, removed }
}

/**
 * One recording scene, as the ripple sees it: where it started and ended
 * before the sync, and how much longer it is now (0 when unchanged; minus
 * its length when it was removed).
 */
export type RippleChange = { start: number; oldEnd: number; delta: number }

/** The shift from each old boundary on, in time order (`rippleOf`). */
export type Ripple = readonly { at: number; shift: number }[]

const EPSILON = 1e-6

/** How far a time moves: the shift of the last boundary at or before it. */
export function shiftAt(at: number, ripple: Ripple): number {
  let shift = 0
  for (const boundary of ripple) {
    if (boundary.at > at + EPSILON) break
    shift = boundary.shift
  }
  return shift
}

/**
 * The ripple of one sync. At each scene's old end, what follows moves by the
 * scene's own change on top of the furthest shift seen while it played:
 * `max(shift at its start, shift at the previous boundary) + delta`, the
 * largest over the scenes ending there. A scene's new end is therefore never
 * past where the items after it land (none is clamped), changes in sequence
 * add up, and changes side by side take the larger rather than the sum.
 */
export function rippleOf(changes: readonly RippleChange[]): Ripple {
  const sorted = [...changes].sort((a, b) => a.oldEnd - b.oldEnd)
  const ripple: { at: number; shift: number }[] = []
  for (let i = 0; i < sorted.length; ) {
    const at = sorted[i]!.oldEnd
    const previous = ripple.at(-1)?.shift ?? 0
    let shift = -Infinity
    for (; i < sorted.length && sorted[i]!.oldEnd <= at + EPSILON; i++) {
      const change = sorted[i]!
      shift = Math.max(
        shift,
        Math.max(shiftAt(change.start, ripple), previous) + change.delta,
      )
    }
    ripple.push({ at, shift })
  }
  return ripple
}

/** An item's span after the ripple: its start and its end move separately. */
export function rippleSpan(
  span: { from: number; duration: number },
  ripple: Ripple,
): { from: number; duration: number } {
  const end = span.from + span.duration
  const from = span.from + shiftAt(span.from, ripple)
  return { from, duration: end + shiftAt(end, ripple) - from }
}

/**
 * One track after the ripple: in order, each item ending no later than the
 * next one starts. An item left shorter than `minDuration` is dropped.
 */
export function clampTrack<
  T extends { id: string; from: number; duration: number },
>(items: readonly T[], minDuration: number): { items: T[]; removed: T[] } {
  const sorted = [...items].sort((a, b) => a.from - b.from)
  const kept: T[] = []
  const removed: T[] = []
  for (let i = 0; i < sorted.length; i++) {
    const item = sorted[i]!
    const next = sorted[i + 1]
    const end = next
      ? Math.min(item.from + item.duration, next.from)
      : item.from + item.duration
    const duration = end - item.from
    if (duration < minDuration - EPSILON) removed.push(item)
    else kept.push(duration === item.duration ? item : { ...item, duration })
  }
  return { items: kept, removed }
}

/**
 * In words, for the editor's toast: what following the transcript took away.
 * A scene that only got shorter or longer is not reported: the timeline
 * shows it, cut markers included. A scene or element that is gone leaves
 * nothing to see, so that is said.
 */
export function describeRemovals({
  removed,
  dropped,
}: {
  removed: number
  dropped: number
}): string[] {
  const out: string[] = []
  if (removed)
    out.push(
      `${removed === 1 ? 'A scene was' : `${removed} scenes were`} removed: everything in ${removed === 1 ? 'it' : 'them'} was cut from the transcript`,
    )
  if (dropped)
    out.push(
      `${dropped === 1 ? 'An element' : `${dropped} elements`} left with no room ${dropped === 1 ? 'was' : 'were'} removed`,
    )
  return out
}

/** A recording scene's window; a legacy item (no `sourceEnd`) is `sourceStart + duration`. */
export function configWindow(item: {
  sourceStart: number
  sourceEnd?: number
  duration: number
}): SceneWindow {
  return {
    start: item.sourceStart,
    end: item.sourceEnd ?? round(item.sourceStart + item.duration, 3),
  }
}

type Fade = { in?: number; out?: number }

function fadeWithin(fade: Fade | undefined, duration: number) {
  if (!fade) return fade
  return {
    ...(fade.in !== undefined && { in: Math.min(fade.in, duration) }),
    ...(fade.out !== undefined && { out: Math.min(fade.out, duration) }),
  }
}

/** An item that got shorter never fades for longer than it lasts. */
function withFadesWithin<T extends TimelineItemAny>(item: T): T {
  const out = { ...item } as T & { fade?: Fade; audioFade?: Fade }
  if ('fade' in out && out.fade) out.fade = fadeWithin(out.fade, out.duration)
  if ('audioFade' in out && out.audioFade)
    out.audioFade = fadeWithin(out.audioFade, out.duration)
  return out
}

function boundTrackV2(item: TimelineItemAny): string | undefined {
  if (item.type === 'captions' || item.type === 'component')
    return 'for' in item ? item.for : undefined
  if (item.type === 'text') {
    const dynamic = item.dynamic
    return dynamic && 'for' in dynamic ? dynamic.for : undefined
  }
  return undefined
}

const isRecordingItem = (item: TimelineItemAny): item is MediaItemAny =>
  isMediaItemAny(item) && item.source.kind === 'recording'

/**
 * The recording window an item plays, if any: a free recording item's or,
 * in generation 3, a recording scene's. These are what follow the cuts.
 */
function recordingWindow(item: TimelineItemAny) {
  const window = itemWindow(item)
  if (!window || window.source.kind !== 'recording') return null
  return { ...window, recordingId: window.source.recordingId }
}

/**
 * `after` without the followers (captions, speaker text and pictures,
 * visualizers) bound to a track that played a recording in `before` and plays
 * none in `after`: nothing is left for them to follow. `removed` counts them.
 */
export function withoutSilencedFollowers<
  T extends { id: string; items: TimelineItemAny[] },
>(before: readonly T[], after: T[]): { tracks: T[]; removed: number } {
  const playing = new Set(
    after.filter((t) => t.items.some(isRecordingItem)).map((t) => t.id),
  )
  const silenced = new Set(
    before
      .filter((t) => t.items.some(isRecordingItem) && !playing.has(t.id))
      .map((t) => t.id),
  )
  if (!silenced.size) return { tracks: after, removed: 0 }
  let removed = 0
  const tracks = after.map((track) => ({
    ...track,
    items: track.items.filter((item) => {
      const bound = boundTrackV2(item)
      const keep = bound === undefined || !silenced.has(bound)
      if (!keep) removed++
      return keep
    }),
  }))
  return { tracks, removed }
}

/**
 * Generation 3 (#541, #566): `tracks` without the followers (captions,
 * speaker texts and pictures, visualizers) whose followed item is not there
 * to read any more, and without the views whose scene is not — it was cut
 * entirely, pruned, or deleted — and with the membership link cleared from
 * any other item whose scene is gone (a title placed for a scene that no
 * longer exists is kept, free). `removed` counts the followers dropped.
 */
export function withoutOrphanedFollowers<
  T extends { id: string; items: TimelineItemV3[] },
>(tracks: readonly T[]): { tracks: T[]; removed: number } {
  const byId = new Map<string, TimelineItemV3>()
  for (const track of tracks)
    for (const item of track.items) byId.set(item.id, item)
  let removed = 0
  let changed = false
  const out = tracks.map((track) => {
    const items: TimelineItemV3[] = []
    for (const item of track.items) {
      const kind = followerKind(item)
      const parent = kind ? parentOf(item) : undefined
      if (kind && parent !== undefined) {
        if (eligibleParent(kind, byId.get(parent))) items.push(item)
        else {
          removed++
          changed = true
        }
        continue
      }
      const scene = sceneOf(item)
      if (scene === undefined || byId.get(scene)?.type === 'scene')
        items.push(item)
      else {
        items.push(withoutScene(item))
        changed = true
      }
    }
    return items.length === track.items.length &&
      items.every((item, i) => item === track.items[i])
      ? track
      : { ...track, items }
  })
  return { tracks: changed ? out : [...tracks], removed }
}

/**
 * Generation 3: a view spans its scene exactly, and captions span the item
 * they follow (#541, #566). After a ripple re-lengthens a scene or a free
 * recording item, what is derived from it follows.
 */
export function withDerivedSpans<
  T extends { id: string; items: TimelineItemV3[] },
>(tracks: readonly T[]): T[] {
  const spans = new Map<string, { from: number; duration: number }>()
  for (const track of tracks)
    for (const item of track.items) spans.set(item.id, item)
  return tracks.map((track) => ({
    ...track,
    items: track.items.map((item) => {
      if (item.type !== 'view' && item.type !== 'captions') return item
      const parent = spans.get(parentOf(item)!)
      if (!parent) return item
      return {
        ...item,
        from: round(parent.from, 3),
        duration: round(parent.duration, 3),
      }
    }),
  }))
}

/** The follower rule of the config's generation (#541). */
function withoutFollowersOfGoneScenes<
  T extends { id: string; items: TimelineItemAny[] },
>(version: 2 | 3, before: readonly T[], after: T[]): T[] {
  if (version === 3)
    return withDerivedSpans(
      withoutOrphanedFollowers(
        after as unknown as Array<{ id: string; items: TimelineItemV3[] }>,
      ).tracks,
    ) as unknown as T[]
  return withoutSilencedFollowers(before, after).tracks
}

/**
 * A persisted config after following the cuts: changed scenes re-lengthed
 * (with `sourceEnd` written, so a legacy window is never re-derived from a
 * shortened duration), every other item rippled, scenes cut entirely
 * removed with the followers they leave bound to a silent track, and each
 * track clamped. `changed` is false, and the config the input itself, when
 * nothing moved; `notices` says what was removed (`describeRemovals`).
 */
export function syncConfigScenes<C extends TimelineConfig>(
  config: C,
  skipsByRecording: Readonly<Record<string, readonly Range[] | undefined>>,
): { config: C; changed: boolean; notices: string[] } {
  const scenes: SyncScene[] = []
  for (const track of config.tracks)
    for (const item of track.items as TimelineItemAny[]) {
      const window = recordingWindow(item)
      if (!window) continue
      scenes.push({
        id: item.id,
        recordingId: window.recordingId,
        window: configWindow(window),
        durationInFrames: secondsToFrames(item.duration),
      })
    }
  const { lengths, removed } = syncScenes(scenes, skipsByRecording)
  if (!lengths.size && !removed.length)
    return { config, changed: false, notices: [] }

  const removedSet = new Set(removed)
  const changes: RippleChange[] = []
  for (const track of config.tracks)
    for (const item of track.items as TimelineItemAny[]) {
      if (!recordingWindow(item)) continue
      const next = lengths.get(item.id)
      changes.push({
        start: item.from,
        oldEnd: item.from + item.duration,
        delta: next
          ? next.seconds - item.duration
          : removedSet.has(item.id)
            ? -item.duration
            : 0,
      })
    }
  const ripple = rippleOf(changes)

  let tracks: Array<
    { id: string; items: TimelineItemAny[] } & Record<string, unknown>
  > = config.tracks.map((track) => ({
    ...track,
    items: track.items
      .filter((item) => !removedSet.has(item.id))
      .map((item): TimelineItemAny => {
        const span = rippleSpan(item, ripple)
        const from = round(Math.max(0, span.from), 3)
        const window = recordingWindow(item)
        if (window) {
          const next = lengths.get(item.id)
          return {
            ...item,
            from,
            duration: next ? next.seconds : item.duration,
            sourceEnd: configWindow(window).end,
          } as TimelineItemAny
        }
        return { ...item, from, duration: round(span.duration, 3) }
      }),
  }))

  // A scene cut entirely takes the followers it leaves with nothing to follow.
  tracks = withoutFollowersOfGoneScenes(config.version, config.tracks, tracks)

  let dropped = 0
  tracks = tracks.map((track) => {
    const clamped = clampTrack(track.items, 1 / FPS)
    dropped += clamped.removed.length
    return {
      ...track,
      items: clamped.items.map((item) =>
        withFadesWithin({ ...item, duration: round(item.duration, 3) }),
      ),
    }
  })

  return {
    config: { ...config, tracks } as C,
    changed: true,
    notices: describeRemovals({ removed: removed.length, dropped }),
  }
}
